<!-- MachineDashboard.vue -->
<template>
  <modal :isOpen="isInspectionModalOpen" @close="isInspectionModalOpen = false">
    <template #header>
      <h3 class="font-bold text-lg ">{{ "Plan an inspection" }}</h3>
    </template>
    {{ inspectionError }}
    <form @submit.prevent="createNewInspection" class="p-4">
      <div class="flex justify-between w-fit space-x-2 items-start mb-4">
        <button type="submit" class="btn btn-sm btn-ghost" @click.prevent="isInspectionModalOpen = false">
          Cancel
        </button>
        <button type="submit" class="btn btn-sm ">
          Create
        </button>
      </div>
    </form>
  </modal>


  <modal :isOpen="isDowntimeModalOpen" @close="isDowntimeModalOpen = false">
    <template #header>
      <h3 class="font-bold text-lg ">{{ "Report a downtime" }}</h3>
    </template>
    {{ inspectionError }}
    <form @submit.prevent="createNewDowntime" class="p-4">
      <div class="flex justify-between w-fit space-x-2 items-start mb-4">
        <button type="submit" class="btn btn-sm btn-ghost" @click.prevent="isDowntimeModalOpen = false">
          Cancel
        </button>
        <button type="submit" class="btn btn-sm ">
          Create
        </button>
      </div>
    </form>
  </modal>


  <div class="@container machine-dashboard py-4">
    <!-- Basic Info Card -->
    <div class="@md:grid md:grid-cols-2 gap-4 space-y-2 mb-6">
      <div class="card border border-gray-200 h-full dark:border-gray-700 p-4 rounded shadow">
        <h3 class="text-lg font-semibold  flex">
          <span>Machine Info</span>
          <span class="mx-2">
          <button class="btn btn-primary btn-xs" @click="toggleModalInfoOpen()">
            +
          </button>
        </span>
        </h3>
        <div class="space-y-2">
          <p>Name: {{ machine?.name }}</p>
          <p>Status:
            <StatusBadge :status="machine?.status"/>
          </p>
          <p>Location: {{ machine?.site?.name || machine?.customer?.name }}</p>
        </div>
      </div>

      <!-- Current Status -->
      <div class="card border border-gray-200 h-full dark:border-gray-700 p-4 rounded shadow">
        <h3 class="text-lg font-semibold">Current Status</h3>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <p class="text-sm text-gray-600">Uptime</p>
            <p class="text-xl">{{ uptimeFormatted }}</p>
          </div>
          <div>
            <p class="text-sm text-gray-600">Last Maintenance</p>
            <p class="text-xl">{{ lastMaintenanceDate }}</p>
          </div>
        </div>
      </div>



    </div>
    <up-down title="incidents" key="incidents"/>
    <br/>
    <up-down title="signals" key="signals"/>
    <br/>
    <up-down title="downtimes" key="downtime"/>
    <div class="my-6"></div>
    <!-- <stats-month/> -->
    <!-- Machine Incidents Timeline -->
    <div class="@md:flex md:grid-cols-2 gap-4 my-6">
      <div class="card border-gray-200 border p-4 rounded-lg shadow-sm  dark:border-gray-700">
        <h3 class="text-lg font-semibold mb-4">Incident History</h3>
        <Timeline :incidents="machine?.incidents"/>
      </div>
      <!-- Maintenance Schedule -->
      <div class="card border-gray-200 border p-4 rounded-lg shadow-sm dark:border-gray-700">
        <MaintenanceCalendar
            @onDateChanged="onEventDateChange"
            @onEventSelect="onSelectEvent"
            :title="'Work orders'"
            :tasks="machine?.tasks"
        />
      </div>
    </div>

    <!-- Suggested Data Collection Section -->
    <div class=" p-4 rounded-lg mb-6">
      <h3 class="text-lg font-semibold mb-2">
        Suggested Sensors
      </h3>
      <p class="text-sm text-gray-600 mb-4">
        To enable predictive maintenance and better monitoring, consider adding:
      </p>
      <ul class="list-disc pl-5">
        <li>
          <a :href="sensorSuggestionUrl('temperature')">Temperature sensors</a>
        </li>
        <li>Vibration sensors</li>
        <li>Power consumption meters</li>
        <li>Operating hours counter</li>
      </ul>
    </div>


    <Modal :is-open="isOpen" @close="isOpen = false">
      {{ machine }}
    </Modal>
  </div>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import StatusBadge from './components/StatusBadge.vue'
import Timeline from './Timeline.vue'
import MaintenanceCalendar from './MaintenanceCalendar.vue'
import {useRoute} from "vue-router";
import UpDown from "./components/updown.vue";
import SignalForm from "~/components/dashboard/signals/signal_form.vue";
import Modal from "~/components/common/modal.vue";
import {PhAlarm, PhTrafficSignal, PhWarning} from "@phosphor-icons/vue";
import {createInspection} from "~/lib/api/inspections";
import {upsertDowntime} from "~/lib/api/machine_downtimes";

let route = useRoute();
let orgUUID = route.params.organization_uuid;
let isOpen = ref(false);
let isInspectionModalOpen = ref(false);
let isDowntimeModalOpen = ref(false);

let inspectionError = ref(null);
// Props
const props = defineProps({
  machineId: {
    type: String,
    required: true
  },
  data: {
    type: Object,
    required: false
  }
})

const sensorSuggestionUrl = (sensor_type) => {
  return `/dashboard/organizations/${orgUUID}/fleet/${props.machineId}/machine_sensors/?sensor_type=${sensor_type}&action=add`
}
// State
const machine = ref({});

// Computed
const uptimeFormatted = computed(() => {
  // This would need to be implemented once we add uptime tracking
  return 'Noxxt tracked yet'
})

const toggleModalInfoOpen = () => {
  isOpen.value = !isOpen.value;
}

const lastMaintenanceDate = computed(() => {
  if (!machine.value?.tasks?.length) return 'No maintenance recorded'

  const lastTask = machine.value.tasks
      .filter(task => task.category === 'maintenance')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

  return lastTask ? new Date(lastTask.created_at).toLocaleDateString() : 'No maintenance recorded'
})

const openInspectionFormModal = () => {
  isInspectionModalOpen.value = !isInspectionModalOpen.value;
  // show dialog to schedule inspection
  //
}

const openDowntimeFormModal = () => {
  isDowntimeModalOpen.value = !isDowntimeModalOpen.value;
  // show dialog to schedule inspection
  //
}

const createNewInspection = async () => {
  let res = await createInspection(orgUUID, {
    inspection: {
      inspectable_id: machine.value.id,
      inspectable_type: 'Machine',
      source: 'manual',
      inspection_type: 'manual',
    }
  });

  if (res.status === 201 || res.status === 200) {
    return res.json();
  } else {
    inspectionError.value = JSON.stringify(res.errors);
  }
  console.log(res);
  // isInspectionModalOpen.value = !isInspectionModalOpen.value;
  // show dialog to schedule inspection
  //
}


const createNewDowntime = async () => {
  let res = await upsertDowntime(orgUUID, machine.value.id, {
    inspection: {
      inspectable_id: machine.value.id,
      inspectable_type: 'Machine',
      source: 'manual',
      inspection_type: 'manual',
    }
  });

  if (res.status === 201 || res.status === 200) {
    return res.json();
  } else {
    inspectionError.value = JSON.stringify(res.errors);
  }
  console.log(res);
}


// Lifecycle
onMounted(() => {
  machine.value = props.data.machine;
})

const onSelectEvent = (event) => {
  alert('onSelectEvent', event.toString());

}
const onEventDateChange = (date) => {
  alert('onEventDateChange', date.toString());
}
</script>